import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Badge,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { subscribeUploadLogs } from '../../redux/QuestionBank/UploadLogsAction';
import { deleteQuestionsBatch, fetchQuestions } from '../../redux/QuestionBank/QuestionsAction';

const UploadHistorySection = ({ folder, title }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(state => state.loginAuth?.userDetails);
  const activeSpecialty = useSelector(state => state.specialty?.activeSpecialty || 'pmr');
  const folderState = useSelector(state => state.uploadLogs?.[folder] || { logs: [], loading: false, error: null });
  const { logs, loading, error } = folderState;

  const [detailsLog, setDetailsLog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingBatchId, setDeletingBatchId] = useState(null);

  useEffect(() => {
    if (!userDetails?.admin) return;
    const unsubscribe = dispatch(subscribeUploadLogs(folder));
    return () => unsubscribe && unsubscribe();
  }, [dispatch, folder, userDetails?.admin, activeSpecialty]);

  const handleDeleteBatch = async (log) => {
    const batchId = log.batchId;
    const count = log.successCount ?? log.questionCount ?? '?';
    const confirm = await Swal.fire({
      title: 'Delete Batch?',
      text: `This will archive all active questions from batch ${batchId} (${count} uploaded). This cannot be easily undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete batch',
      cancelButtonText: 'Cancel'
    });
    if (!confirm.isConfirmed) return;

    setDeletingBatchId(batchId);
    const result = await dispatch(deleteQuestionsBatch(batchId));
    setDeletingBatchId(null);

    if (result.success) {
      Swal.fire('Batch Deleted', `${result.deletedCount} question(s) archived.${result.failedCount > 0 ? ` ${result.failedCount} failed.` : ''}`, 'success');
      dispatch(fetchQuestions());
    } else {
      Swal.fire('Error', result.error || 'Failed to delete batch.', 'error');
    }
  };

  if (!userDetails?.admin) return null;

  const filteredLogs = searchTerm
    ? logs.filter(log =>
        (log.batchId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (log.fileName || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : logs;

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  return (
    <>
      <Card className="shadow mt-4">
        <CardHeader>
          <Row className="align-items-center">
            <Col md="6">
              <h3 className="mb-0">{title}</h3>
            </Col>
            <Col md="6">
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="fas fa-search"></i>
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  type="text"
                  placeholder="Search by batch ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          {loading && (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading history...</p>
            </div>
          )}
          {error && (
            <div className="text-center py-5">
              <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
              <h4 className="text-muted">Failed to load history</h4>
              <p className="text-muted">{error}</p>
            </div>
          )}
          {!loading && !error && logs.length === 0 && (
            <div className="text-center py-5">
              <i className="fas fa-history fa-3x text-muted mb-3"></i>
              <h4 className="text-muted">No Upload History Yet</h4>
              <p className="text-muted">Upload history will appear here after the first batch.</p>
            </div>
          )}
          {!loading && logs.length > 0 && filteredLogs.length === 0 && (
            <div className="text-center py-5">
              <i className="fas fa-search fa-3x text-muted mb-3"></i>
              <h4 className="text-muted">No Results Found</h4>
              <p className="text-muted">Try adjusting your search term.</p>
            </div>
          )}
          {!loading && filteredLogs.length > 0 && (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Batch ID</th>
                  <th>File</th>
                  <th>Uploaded By</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Success</th>
                  <th>Failed</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <small className="font-weight-bold">{log.batchId}</small>
                    </td>
                    <td>
                      <small>{log.fileName || '—'}</small>
                    </td>
                    <td>
                      <small>{log.uploadedBy || '—'}</small>
                    </td>
                    <td>
                      <small>{formatDate(log.uploadedAt)}</small>
                    </td>
                    <td>
                      <Badge color="secondary">{log.totalRows ?? log.questionCount ?? '—'}</Badge>
                    </td>
                    <td>
                      <Badge color="success">{log.successCount ?? '—'}</Badge>
                    </td>
                    <td>
                      <Badge color={log.failCount > 0 ? 'danger' : 'light'}>
                        {log.failCount ?? '—'}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <Button
                        size="sm"
                        color="primary"
                        className="mr-1"
                        disabled={!log.downloadUrl}
                        onClick={() => window.open(log.downloadUrl, '_blank')}
                      >
                        <i className="fas fa-download mr-1"></i>
                        Download
                      </Button>
                      <Button
                        size="sm"
                        color="secondary"
                        onClick={() => setDetailsLog(log)}
                      >
                        <i className="fas fa-info-circle mr-1"></i>
                        Details
                      </Button>
                      {log.batchId && (
                        <Button
                          size="sm"
                          color="danger"
                          className="ml-1"
                          disabled={deletingBatchId === log.batchId}
                          onClick={() => handleDeleteBatch(log)}
                        >
                          {deletingBatchId === log.batchId ? (
                            <><Spinner size="sm" className="mr-1" /> Deleting...</>
                          ) : (
                            <><i className="fas fa-trash mr-1"></i>Delete Batch</>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Details Modal */}
      <Modal isOpen={!!detailsLog} toggle={() => setDetailsLog(null)} size="lg">
        <ModalHeader toggle={() => setDetailsLog(null)}>
          Batch Details — {detailsLog?.batchId}
        </ModalHeader>
        <ModalBody>
          {detailsLog && (
            <>
              <dl className="row">
                <dt className="col-sm-4">Batch ID</dt>
                <dd className="col-sm-8">{detailsLog.batchId}</dd>

                <dt className="col-sm-4">Date</dt>
                <dd className="col-sm-8">{formatDate(detailsLog.uploadedAt)}</dd>

                <dt className="col-sm-4">Uploaded By</dt>
                <dd className="col-sm-8">{detailsLog.uploadedBy || '—'}</dd>

                <dt className="col-sm-4">File</dt>
                <dd className="col-sm-8">{detailsLog.fileName || '—'}</dd>

                {/* full_length specific fields */}
                {detailsLog.examId && (
                  <>
                    <dt className="col-sm-4">Exam ID</dt>
                    <dd className="col-sm-8">{detailsLog.examId}</dd>
                  </>
                )}
                {detailsLog.examName && (
                  <>
                    <dt className="col-sm-4">Exam Name</dt>
                    <dd className="col-sm-8">{detailsLog.examName}</dd>
                  </>
                )}
                {detailsLog.questionCount !== undefined && (
                  <>
                    <dt className="col-sm-4">Question Count</dt>
                    <dd className="col-sm-8">{detailsLog.questionCount}</dd>
                  </>
                )}

                {/* bulk_upload / bulk_edit specific fields */}
                {detailsLog.totalRows !== undefined && (
                  <>
                    <dt className="col-sm-4">Total Rows</dt>
                    <dd className="col-sm-8">{detailsLog.totalRows}</dd>
                  </>
                )}
                {detailsLog.successCount !== undefined && (
                  <>
                    <dt className="col-sm-4">Success</dt>
                    <dd className="col-sm-8">
                      <Badge color="success">{detailsLog.successCount}</Badge>
                    </dd>
                  </>
                )}
                {detailsLog.failCount !== undefined && (
                  <>
                    <dt className="col-sm-4">Failed</dt>
                    <dd className="col-sm-8">
                      <Badge color={detailsLog.failCount > 0 ? 'danger' : 'light'}>
                        {detailsLog.failCount}
                      </Badge>
                    </dd>
                  </>
                )}
                {detailsLog.updatedCount !== undefined && (
                  <>
                    <dt className="col-sm-4">Updated</dt>
                    <dd className="col-sm-8">{detailsLog.updatedCount}</dd>
                  </>
                )}
                {detailsLog.createdCount !== undefined && (
                  <>
                    <dt className="col-sm-4">Created</dt>
                    <dd className="col-sm-8">{detailsLog.createdCount}</dd>
                  </>
                )}
              </dl>

              {/* Question codes */}
              {detailsLog.questionCodes && detailsLog.questionCodes.length > 0 && (
                <>
                  <h6 className="mt-3">Question Codes</h6>
                  <p className="text-monospace small">
                    {detailsLog.questionCodes.join(', ')}
                  </p>
                </>
              )}

              {/* Errors list */}
              {detailsLog.errors && detailsLog.errors.length > 0 && (
                <>
                  <h6 className="mt-3 text-danger">Errors</h6>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsLog.errors.map((err, i) => (
                        <tr key={i}>
                          <td>{err.row ?? '—'}</td>
                          <td>{err.error || String(err)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {detailsLog.downloadUrl && (
                <div className="mt-3">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => window.open(detailsLog.downloadUrl, '_blank')}
                  >
                    <i className="fas fa-download mr-2"></i>
                    Download Original File
                  </Button>
                </div>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default UploadHistorySection;
